import React, {
  useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import styled from 'styled-components';
import { useMutation, useQuery } from '@apollo/react-hooks';
import { AppContext } from '../../context/AppContext';
import { UserContext } from '../../context/UserContext';
import {
  DISMISS_ONBOARDING_COMPLETE_MODAL,
  GET_CURRENT_ORGANIZATION,
} from '../../graphql/queries';
import { useDesktopMedia } from '../../util/MediaQuery';
import { isGuidedOnboardingEnabled } from '../../util/guidedOnboarding';
import AccessLevels from '../../util/AccessLevels';
import colors from '../../styles/colors';
import Icon from '../Icon';
import { IconEnum as Icons } from '../Icons';
import {
  getVisibleChecklistSteps,
  isStepComplete,
  organizationSmsCampaignsEnabled,
} from './checklistConfig';

const Panel = styled.div`
  position: fixed;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  z-index: 1005;
  width: min(380px, calc(100vw - 32px));
  box-sizing: border-box;
  background: linear-gradient(to right, #32618f, #231f20);
  border-radius: 8px;
  padding: 18px 20px 16px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.35);
  pointer-events: auto;
`;

const TopRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
`;

const IconTitle = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  min-width: 0;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 17px;
  font-weight: 600;
  color: ${colors.white};
  line-height: 1.35;
`;

const Body = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.55;
  color: ${colors.white};
  opacity: 0.95;
`;

const ReplayBody = styled.p`
  margin: 14px 0 0;
  font-size: 14px;
  line-height: 1.55;
  color: ${colors.white};
  opacity: 0.95;
`;

const CloseBtn = styled.button`
  flex-shrink: 0;
  margin: 0;
  padding: 4px;
  border: none;
  background: transparent;
  cursor: pointer;
  line-height: 0;
  border-radius: 4px;
  &:hover {
    opacity: 0.85;
  }
`;

const FooterRule = styled.div`
  margin-top: 16px;
  height: 6px;
  border-radius: 2px;
  background: repeating-linear-gradient(
    90deg,
    ${colors.seaGreen} 0,
    ${colors.seaGreen} 10px,
    transparent 10px,
    transparent 16px
  );
  opacity: 0.55;
`;

const FooterActions = styled.div`
  margin-top: 14px;
  display: flex;
  justify-content: center;
`;

const DoneButton = styled.button`
  margin: 0;
  border: none;
  border-radius: 6px;
  height: 36px;
  padding: 0 16px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  color: ${colors.white};
  background: ${colors.seaGreen};
  &:hover {
    opacity: 0.92;
  }
`;

const TITLE = 'Congrats, You Finished Your Account Setup!';
const BODY_SMS_ENABLED = "We'll let you know in 2-7 days once your account is verified and ready to send your first campaign.";
const BODY_SMS_DISABLED = 'Let us know if you have any questions about how to get the most out of your Check This Out membership!';
const REPLAY_BODY = 'After clicking Done below, you can re-open this onboarding flow at any time by clicking the compass button in the sidebar.';

/**
 * Celebration when the guided checklist is fully complete. Opens from checklist progress and/or a
 * post-tour bump so it is not blocked by stale `UserContext` or `allComplete` timing after mutations.
 */
export default function OnboardingCompleteModal() {
  const { app } = useContext(AppContext);
  const { user } = useContext(UserContext);
  const isDesktop = useDesktopMedia();
  const [open, setOpen] = useState(false);
  const bumpBaselineRef = useRef({ orgId: null, lastBump: 0 });

  const bump = app?.onboarding?.onboardingCompleteModalBump ?? 0;

  const { data: orgQueryData } = useQuery(GET_CURRENT_ORGANIZATION, {
    skip: !user?._id,
    fetchPolicy: 'cache-first',
  });

  /** Same cache as tour refetch — avoids lag vs UserContext (synced in AdminLayout useEffect). */
  const org = orgQueryData?.getCurrentOrganization ?? user?.organization;
  const orgId = org?._id;
  const progress = org?.onboardingChecklistProgress;
  const role = user?.currentOrganizationRole;
  const modalDismissed = !!org?.onboardingCompleteModalDismissed;

  const checklistFullyDone = useMemo(() => {
    if (!progress) return false;
    if (progress.allComplete) return true;
    const steps = getVisibleChecklistSteps(org);
    return steps.every((s) => isStepComplete(s.id, progress));
  }, [org, progress]);

  const mayOfferModal = isGuidedOnboardingEnabled(user)
    && isDesktop
    && (role === AccessLevels.Admin || role === AccessLevels.SuperAdmin);

  const [dismissModal] = useMutation(DISMISS_ONBOARDING_COMPLETE_MODAL, {
    refetchQueries: [{ query: GET_CURRENT_ORGANIZATION }],
    awaitRefetchQueries: false,
  });

  useEffect(() => {
    if (!mayOfferModal || !orgId) {
      setOpen(false);
      return;
    }
    if (modalDismissed) {
      setOpen(false);
      return;
    }

    if (bumpBaselineRef.current.orgId !== orgId) {
      bumpBaselineRef.current = { orgId, lastBump: bump };
      if (checklistFullyDone) {
        setOpen(true);
      } else {
        setOpen(false);
      }
      return;
    }

    if (bump > bumpBaselineRef.current.lastBump) {
      bumpBaselineRef.current.lastBump = bump;
      setOpen(true);
      return;
    }

    if (checklistFullyDone) {
      setOpen(true);
    } else {
      setOpen(false);
    }
  }, [bump, orgId, checklistFullyDone, mayOfferModal, modalDismissed]);

  const dismiss = useCallback(() => {
    if (orgId && !modalDismissed) {
      dismissModal().catch((e) => {
        console.error(e);
      });
    }
    setOpen(false);
  }, [orgId, modalDismissed, dismissModal]);

  if (!open || !mayOfferModal || !orgId) {
    return null;
  }

  const smsOn = organizationSmsCampaignsEnabled(org);

  return (
    <Panel role="dialog" aria-labelledby="onboarding-complete-title" aria-modal="true">
      <TopRow>
        <IconTitle>
          <Icon
            icon={Icons.GrinStarsSolid}
            size={26}
            margin="2px 0 0 0"
            active
            activeColor={colors.white}
          />
          <Title id="onboarding-complete-title">{TITLE}</Title>
        </IconTitle>
        <CloseBtn type="button" onClick={dismiss} aria-label="Close">
          <Icon icon={Icons.TimesCircleSolid} size={20} active activeColor={colors.white} />
        </CloseBtn>
      </TopRow>
      <Body>{smsOn ? BODY_SMS_ENABLED : BODY_SMS_DISABLED}</Body>
      <ReplayBody>{REPLAY_BODY}</ReplayBody>
      <FooterRule aria-hidden />
      <FooterActions>
        <DoneButton type="button" onClick={dismiss}>
          Done
        </DoneButton>
      </FooterActions>
    </Panel>
  );
}
